/**
 * KW-584 — Anbindung des Bundle-Splits an den Warenkorb des Themes.
 *
 * Hängt sich in `CartItems.updateQuantity()` (Warenkorbseite) und `MiniCart`
 * ein: Bei AVIS-Bundles mit Stück-Semantik bedeutet „Menge 2" nicht mehr
 * „Komponentenmengen verdoppeln", sondern „zweite Konfigurationsgruppe im
 * Warenkorb". Die Zeilen der zusätzlichen Gruppen werden serverseitig in
 * `main-cart-items.liquid` / `header-mini-cart-content.liquid` ausgeblendet —
 * der Kunde sieht weiterhin eine Zeile mit Menge 2.
 *
 * Meterware (`data-kw-split="false"`) behält das bisherige Verhalten, dort ist
 * die Menge eine Länge und darf zusammengefasst werden.
 *
 * Lädt nach scripts.js (defer), damit die Custom Elements registriert sind.
 */
(function () {
  'use strict';

  var DEBUG = /[?&]kw-split-debug=1/.test(window.location.search);
  function log() {
    if (DEBUG && window.console) console.log.apply(console, ['[kw-584]'].concat([].slice.call(arguments)));
  }

  function cartRoute(name, fallback) {
    var r = window.routes || {};
    return r[name] || fallback;
  }

  /**
   * Die Cart AJAX API antwortet auf abgelehnte Anfragen (z. B. Bestandsgrenze)
   * mit 4xx und einem `description`-Feld. Ohne diese Prüfung würde die
   * Ablehnung stillschweigend geschluckt und die Menge im Warenkorb spränge
   * kommentarlos auf den alten Wert zurück.
   */
  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (json) {
        if (!res.ok) {
          var err = new Error((json && (json.description || json.message)) || ('HTTP ' + res.status));
          err.cartResponse = json;
          throw err;
        }
        return json;
      });
    });
  }

  var api = {
    get: function () {
      return fetch(cartRoute('cart_url', '/cart') + '.js', {
        headers: { Accept: 'application/json' }
      }).then(function (res) { return res.json(); });
    },
    add: function (body) {
      return postJSON(cartRoute('cart_add_url', '/cart/add') + '.js', body)
        .then(function () { return api.get(); });
    },
    update: function (body) {
      return postJSON(cartRoute('cart_update_url', '/cart/update') + '.js', body);
    }
  };

  /**
   * Mengen-Semantik je Variante. Die Werte stehen als `data-kw-split` an den
   * gerenderten Bundle-Zeilen (aus dem Metafeld `trigger.mengen_semantik`,
   * ersatzweise aus dem Produkttitel „Meterware").
   */
  function semantics(parentItem) {
    var row = document.querySelector('[data-kw-variant="' + parentItem.variant_id + '"]');
    if (row && row.dataset.kwSplit === 'false') return 'meter';
    return 'stueck';
  }

  /**
   * Sektionen neu rendern. Bewusst über die theme-eigene Methode: der Mini-Cart
   * liefert Sektionen ohne `selector`, was nur `getSectionInnerHTML()` mit
   * seinem Default `.shopify-section` korrekt auflöst — eine eigene Render-
   * schleife lässt den Warenkorb-Drawer sonst unverändert stehen.
   */
  function refresh(host) {
    var rendered = typeof host.updateSectionContents === 'function'
      ? host.updateSectionContents()
      : Promise.resolve();

    return rendered.then(api.get).then(function (cart) {
      host.classList.toggle('is-empty', cart.item_count === 0);
      var footer = document.getElementById('main-cart-footer');
      if (footer) footer.classList.toggle('is-empty', cart.item_count === 0);

      document.dispatchEvent(new CustomEvent(
        cart.item_count === 0 ? 'cart:item-remove' : 'cart:item-quantity-update',
        { detail: { cart: cart, context: host.eventContext } }
      ));

      return cart;
    });
  }

  function rowFor(line) {
    return document.getElementById('CartItem-' + line) ||
      document.getElementById('MiniCartItem-' + line);
  }

  /**
   * Mengenänderungen strikt nacheinander. Dieselbe Eingabe kann doppelt
   * ankommen (Enter + change, schnelles +/-); liefen zwei setQuantity-Aufrufe
   * parallel, sähe der zweite einen halb aufgestockten Warenkorb und legte
   * zusätzliche Gruppen an (3 gewünscht, 4 im Warenkorb). Jeder Aufruf liest
   * den Warenkorb frisch und setzt eine absolute Zielmenge — nacheinander
   * ausgeführt ist ein doppelter Aufruf damit ein No-op.
   */
  var queue = Promise.resolve();

  function patch(tagName) {
    var El = window.customElements && customElements.get(tagName);
    if (!El || El.prototype.__kwSplitPatched) return false;

    var original = El.prototype.updateQuantity;
    if (typeof original !== 'function') return false;

    El.prototype.updateQuantity = function (line, quantity, name) {
      var row = rowFor(line);
      var unitKey = row && row.dataset.kwUnitKey;
      var splittable = row && row.dataset.kwSplit === 'true';

      if (!unitKey || !splittable) return original.call(this, line, quantity, name);

      var host = this;
      var want = parseInt(quantity, 10);
      if (isNaN(want) || want < 0) return original.call(this, line, quantity, name);
      log('setQuantity queued', { line: line, unitKey: unitKey, want: want });

      if (typeof host.enableLoading === 'function') host.enableLoading(line);

      var run = function () {
        return runSetQuantity(host, unitKey, want);
      };
      queue = queue.then(run, run);
      return queue;
    };

    El.prototype.__kwSplitPatched = true;
    log('patched', tagName);
    return true;
  }

  function runSetQuantity(host, unitKey, want) {
      log('setQuantity', { unitKey: unitKey, want: want });
      return window.CdxBundleSplit.setQuantity(api, unitKey, want, semantics)
        .then(function () { return refresh(host); })
        .then(function (cart) {
          // Wurde die gewünschte Menge wirklich erreicht? Wenn nicht, sähe der
          // Kunde nur, wie die Zahl kommentarlos zurückspringt.
          var state = window.CdxBundleSplit.analyze(cart, semantics);
          var reached = null;
          state.units.forEach(function (u) { if (u.signature === unitKey) reached = u.quantity; });
          if (reached !== null && reached !== want && typeof host.setError === 'function') {
            host.setError('Menge konnte nicht auf ' + want + ' gesetzt werden (aktuell ' + reached + ').');
          }
          log('done', { want: want, reached: reached });
          return cart;
        })
        .catch(function (err) {
          log('failed', err);
          if (typeof host.setError === 'function') {
            host.setError((err && err.message) || (window.cartStrings && window.cartStrings.error) || 'Fehler');
          }
          return refresh(host);
        })
        .finally(function () {
          if (typeof host.disableLoading === 'function') host.disableLoading();
        });
  }

  /**
   * Enter im Mengenfeld sendet sonst das Warenkorb-Formular ab. Dessen
   * `updates[]` werden von Shopify POSITIONSBASIERT auf die Warenkorbzeilen
   * gelegt — die ausgeblendeten AVIS-Komponentenzeilen haben aber gar kein
   * Mengenfeld, also verrutscht die Zuordnung und die Eingabe verpufft
   * (die Menge springt kommentarlos auf den alten Wert zurück).
   *
   * Enter wird deshalb auf denselben AJAX-Weg geleitet wie die +/- Buttons.
   * Der Checkout-Button bleibt unangetastet, er muss das Formular absenden.
   */
  function guardEnterKey() {
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' && event.keyCode !== 13) return;
      var field = event.target && event.target.closest && event.target.closest('.quantity-input-field');
      if (!field) return;
      event.preventDefault();
      log('enter abgefangen', field.value);
      field.dispatchEvent(new Event('change', { bubbles: true }));
    }, true);
  }

  /**
   * Avis-Gruppen, die der Konfigurator ohne Verschachtelung angelegt hat,
   * einmalig als Nested Cart Lines neu anlegen (Checkout zeigt Komponenten
   * dann unter dem Parent). Danach die Warenkorb-Sektionen neu rendern.
   * Laeuft nur, wenn eine Warenkorb-Komponente auf der Seite ist.
   */
  var normalizing = false;
  function normalizeNesting(reason) {
    if (normalizing || !window.CdxBundleSplit) return;
    var host = document.querySelector('cart-items') || document.querySelector('mini-cart');
    if (!host) return;
    normalizing = true;
    var run = function () {
      return window.CdxBundleSplit.normalize(api)
        .then(function (res) {
          log('normalize', { reason: reason, changed: res.changed });
          if (res.changed > 0) return refresh(host);
        })
        .catch(function (err) { log('normalize failed', err); })
        .finally(function () { normalizing = false; });
    };
    queue = queue.then(run, run);
  }

  function patchAll() {
    if (!window.CdxBundleSplit) return false;
    var a = patch('cart-items');
    var b = patch('mini-cart');
    return a || b;
  }

  guardEnterKey();

  if (!patchAll()) {
    // scripts.js noch nicht ausgeführt — auf die Registrierung warten.
    var tries = 0;
    var timer = setInterval(function () {
      if (patchAll() || ++tries > 40) clearInterval(timer);
    }, 50);
  }

  // Nach dem Laden und nach jedem Hinzufuegen (Avis-Popup, Quick-Add) pruefen,
  // ob neue Gruppen ohne Verschachtelung im Warenkorb liegen.
  function scheduleNormalize(reason) {
    setTimeout(function () { normalizeNesting(reason); }, 0);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scheduleNormalize('load'); });
  } else {
    scheduleNormalize('load');
  }
  document.addEventListener('cart:item-add', function () { scheduleNormalize('item-add'); });
})();
